/**
 * One point of a portfolio valuation series, oldest first.
 *
 * Declared structurally rather than imported so this can be used from anywhere that reads
 * `return_valuations` - the Portfolio view, the AI chat context builders - without either
 * side depending on the other. `ReturnValuationPerformance` satisfies it as-is.
 */
export interface ValuationPoint {
  /** Portfolio value (NAV) at this date. */
  currentHoldings: number;
  /** Cumulative net capital invested at this date. */
  netContributions: number;
  /** Published holding return for the month ending at this date, in percent. */
  oneMonthReturn: number | null;
}

export interface PeriodReturn {
  /** Return over the period, in percent. Null when there is not enough history to state one. */
  percent: number | null;
  /** How many months of history actually went into the number. */
  monthsUsed: number;
  /** True when the portfolio is younger than the requested period. */
  isPartialPeriod: boolean;
}

const UNAVAILABLE: PeriodReturn = { percent: null, monthsUsed: 0, isPartialPeriod: true };

/**
 * Return of the portfolio over a window, from the valuation series.
 *
 * Preferred path is to chain the monthly holding returns the data team already publishes
 * (`oneMonthHoldingReturn`), which is what the Performance Story chart is built on. Chaining
 * them geometrically - not adding them - is what makes 12M consistent with twelve 1M steps.
 *
 * When a month in the window has no published return we fall back to Modified Dietz across
 * the whole window, which strips out money paid in and taken out so that a client topping up
 * their portfolio does not register as performance:
 *
 *   R = (V_end - V_start - netFlow) / (V_start + 0.5 x netFlow)
 *
 * The 0.5 assumes contributions land evenly through the window; it is the standard
 * approximation when the timing of individual flows is not available.
 */
export const calculatePeriodReturn = (valuations: ValuationPoint[], months: number): PeriodReturn => {
  if (!valuations || valuations.length < 2) return UNAVAILABLE;

  // Series arrives oldest-first; the window is the last `months` steps.
  const startIndex = Math.max(0, valuations.length - 1 - months);
  const window = valuations.slice(startIndex);
  const monthsUsed = window.length - 1;

  if (monthsUsed <= 0) return UNAVAILABLE;

  const isPartialPeriod = monthsUsed < months;
  const steps = window.slice(1); // the return of each month is stamped on its end point

  if (steps.every((point) => typeof point.oneMonthReturn === 'number' && !Number.isNaN(point.oneMonthReturn))) {
    const growth = steps.reduce((acc, point) => acc * (1 + (point.oneMonthReturn as number) / 100), 1);
    return { percent: (growth - 1) * 100, monthsUsed, isPartialPeriod };
  }

  // Fallback: Modified Dietz over the window.
  const start = window[0];
  const end = window[window.length - 1];
  const netFlow = end.netContributions - start.netContributions;
  const denominator = start.currentHoldings + 0.5 * netFlow;

  if (denominator <= 0) return { percent: null, monthsUsed, isPartialPeriod };

  const gain = end.currentHoldings - start.currentHoldings - netFlow;
  return { percent: (gain / denominator) * 100, monthsUsed, isPartialPeriod };
};

export const PERIODS = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '12M', months: 12 },
] as const;

export const calculatePeriodReturns = (valuations: ValuationPoint[]) =>
  PERIODS.map(({ label, months }) => ({ label, months, ...calculatePeriodReturn(valuations, months) }));

interface PricedHolding {
  totalValue: number;
  change: number | null | undefined;
}

/**
 * Market price movement of a set of holdings, weighted by each one's share of the total.
 *
 * This is deliberately NOT a portfolio return, and must never be presented as one: it only
 * sees wines held right now, so it misses anything sold during the period and credits a wine
 * with gains it made before the client bought it. It also ignores cash, fees and contributions.
 * For performance use calculatePeriodReturn on the valuation series.
 *
 * Weights come from each wine's value at the START of the period (derived by backing out the
 * price change), so a large steady holding outweighs a tiny volatile one. Wines with no price
 * history are dropped from both sides rather than counted as 0%, which would otherwise drag
 * the number towards zero.
 */
export const valueWeightedPriceChange = (holdings: PricedHolding[]): number | null => {
  let valueNow = 0;
  let valueThen = 0;

  holdings.forEach(({ totalValue, change }) => {
    if (!totalValue || totalValue <= 0) return;
    if (typeof change !== 'number' || Number.isNaN(change) || change <= -100) return;
    valueNow += totalValue;
    valueThen += totalValue / (1 + change / 100);
  });

  return valueThen > 0 ? (valueNow / valueThen - 1) * 100 : null;
};
